import { useState, useEffect, useContext } from 'react';
import { t } from 'i18next';
import {
  AccordionHeader,
  GlobalSuccess,
  GlobalWarningAlert,
} from '@sentara-health/optimahealth_web';
import {
  UserImageAvatar,
  ProfileHeaderContent,
  HomeAddressDetails,
  EmailChange,
} from 'templates';
import {
  UserName,
  AlternativeAddressDetails,
  ChangeHomeAddressDetails,
} from 'organisms';
import { ProfileImageProps } from './interface';
import {
  formatePhoneNo,
  DeviceTypes,
  useDeviceType,
  addressFormatter,
  toSentancePascalCase,
  domainForSegment,
  ANALYTICS_MESSAGES,
  useAnalytics,
} from 'common';
import PrimaryPhoneNumberComponent from './PrimaryPhoneNumberComponent';
import { GlobalContext } from 'context';

function ProfileImage({ state, setState }: Readonly<ProfileImageProps>) {
  const [showHomeEdit, setShowHomeEdit] = useState(true);
  const [showChangeAddressInfo, setShowChangeAddressInfo] = useState(false);
  const [showEmailEdit, setShowEmailEdit] = useState(true);
  const [success, setSuccess] = useState(false);
  const [email, setEmail] = useState('');
  const { onEmailUpdate, getFeature } = useContext(GlobalContext);
  const { trackContentListing } = useAnalytics();
  const deviceType = useDeviceType();
  const isMobile = deviceType === DeviceTypes.MOBILE;

  useEffect(() => {
    setEmail(state?.email ?? '');
  }, [state?.email]);

  useEffect(() => {
    if (onEmailUpdate) {
      setShowEmailEdit(false);
    }
  }, [onEmailUpdate]);

  const handleSuccess = (email: string) => {
    setShowEmailEdit(true);
    setEmail(email);
    setSuccess(true);
  };

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(false);
      }, 6000);

      return () => clearTimeout(timer);
    }
  }, [success]);

  return (
    <section className="set_my_preferences profile_image_wrapper pb-2">
      {!isMobile ? (
        <AccordionHeader
          dataTestId="accordionHeader"
          initialActiveIndex={null}
          title={t('profile.profileDetails')}
        />
      ) : null}
      <div className="col-lg-12 col-md-12 col-sm-12 col-12 border_wrapper">
        <div className="d-flex row px-3">
          <div className="col-lg-3 col-md-4 col-sm-12 col-12 pt-0 p-0">
            <UserImageAvatar
              firstName={state?.firstName}
              lastName={state?.lastName}
            />
          </div>

          <div className="col-lg-9 col-md-8 col-sm-12 col-12 user_address_wrapper">
            <UserName
              firstName={state?.firstName ?? ''}
              lastName={state?.lastName ?? ''}
            />
            <hr />

            <PrimaryPhoneNumberComponent
              t={t}
              phoneNumber={formatePhoneNo(
                state?.phones?.find(
                  (phone: { phoneType: string; phoneNumber: string }) =>
                    phone.phoneType === 'Home'
                )?.phoneNumber ?? '-'
              )}
            />
            <hr />
            <ProfileHeaderContent
              data-testid="profile-header-component"
              header={t('common.emailAddress')}
              contentOne={email ?? ''}
              dataTestIdOne="profileName"
              contentClassNameOne={'_disabled mb-3 mb-lg-0 mb-md-0'}
              showButton={showEmailEdit}
              onClick={() => {
                if (showEmailEdit) {
                  trackContentListing(
                    '602',
                    ANALYTICS_MESSAGES.EMAIL_EDIT_CLICK,
                    2,
                    `${domainForSegment}/profile`
                  );
                }
                setShowEmailEdit(!showEmailEdit);
              }}
              disableButton={!state?.isMemberEnrolled}
            />
            {!showEmailEdit ? (
              <>
                {!state?.isEmailValid && getFeature?.EmailBounceBack && (
                  <div className="mt-0 mt-lg-3 mt-md-3">
                    <GlobalWarningAlert>
                      {t('profile.emailUpdateMessage')}
                    </GlobalWarningAlert>
                  </div>
                )}
                <EmailChange
                  emailId={email ?? ''}
                  onClickCancel={() => {
                    setShowEmailEdit(true);
                  }}
                  handleSuccess={handleSuccess}
                />
              </>
            ) : null}

            {success && (
              <div className="fade-toast">
                <GlobalSuccess variant="mb-3" dataTestId="usernameServiceErr12">
                  {t('profile.emailSuccess')}
                </GlobalSuccess>
              </div>
            )}

            <hr />
            <div className="row align-items-center">
              {showHomeEdit ? (
                <ProfileHeaderContent
                  header={t('profile.homeAddress')}
                  contentOne={addressFormatter(
                    state?.address?.[0]?.street ?? ''
                  )}
                  contentTwo={` ${
                    state?.address?.[0]?.city
                      ? `${toSentancePascalCase(state?.address?.[0]?.city)}, `
                      : ''
                  }
                        ${state?.address?.[0]?.state?.toUpperCase() ?? ''}
                        ${state?.address?.[0]?.zipCode ?? ''}`}
                  contentClassNameTwo={'mt-1 mb-md-0'}
                  contentClassNameOne={'mb-1 mb-md-0'}
                  dataTestIdOne="userAddressStreet"
                  dataTestIdTwo="userAddress"
                  showChangeAddress={true}
                  showButton={getFeature?.HomeAddressEdit}
                  onClick={() => setShowHomeEdit(!showHomeEdit)}
                  onChangeAddress={() => setShowChangeAddressInfo(true)}
                />
              ) : (
                <HomeAddressDetails
                  canChangeContactInfo={getFeature?.HomeAddressEdit}
                  city={state?.address?.[0]?.city ?? ''}
                  dependents={state?.dependents || []} // Update the type of dependents prop
                  street={state?.address?.[0]?.street ?? ''}
                  zipCode={state?.address?.[0]?.zipCode ?? ''}
                  state={state?.address?.[0]?.state ?? ''}
                  closeComponent={() => setShowHomeEdit(!showHomeEdit)}
                  setState={setState}
                />
              )}
            </div>
            {showChangeAddressInfo && <ChangeHomeAddressDetails />}
            {getFeature?.AlternateAddress ? (
              <>
                <hr />
                <AlternativeAddressDetails />
              </>
            ) : null}
            {isMobile ? <hr className="mt-4" /> : null}
          </div>
        </div>
      </div>
    </section>
  );
}

export default ProfileImage;
